import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Wallet, TrendingUp, TrendingDown, RefreshCw, PlugZap } from "lucide-react";

const money = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 4 }));
const pnlColor = (v) => (Number(v) > 0 ? "var(--long)" : Number(v) < 0 ? "var(--short)" : "var(--text-secondary)");

export default function PositionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/bingx/positions");
      setData(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 10000);
    return () => clearInterval(timer.current);
  }, []);

  const refresh = async () => { setLoading(true); await load(); setLoading(false); };

  const acc = data?.account;
  const positions = data?.positions || [];

  return (
    <div data-testid="positions-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-400" />
          <div>
            <h3 className="text-sm font-bold">Borsa Pozisyonları <span className="text-emerald-400">(BingX{data?.env === "prod-live" ? " · CANLI" : data?.env === "prod-vst" ? " · DEMO" : ""})</span></h3>
            <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>Açık pozisyonlar ve canlı PnL · 10 sn'de bir</p>
          </div>
        </div>
        <button data-testid="positions-refresh" onClick={refresh} disabled={loading}
          className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition-colors hover:brightness-125 disabled:opacity-50"
          style={{ borderColor: "var(--border-subtle)", color: "var(--text-secondary)" }}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile
        </button>
      </div>

      {data && !data.configured ? (
        <div className="p-6 text-center text-sm flex flex-col items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <PlugZap className="w-6 h-6" /> BingX API anahtarları eklenmedi.
        </div>
      ) : data?.error ? (
        <div className="p-6 text-center text-sm" style={{ color: "var(--short)" }} data-testid="positions-error">Hata: {data.error}</div>
      ) : (
        <>
          {acc && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 sm:p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              {[
                { label: "Bakiye", value: `${money(acc.balance)} USDT`, color: "var(--text-primary)" },
                { label: "Özkaynak (Equity)", value: `${money(acc.equity)} USDT`, color: "var(--text-primary)" },
                { label: "Kullanılabilir", value: `${money(acc.available)} USDT`, color: "var(--entry)" },
                { label: "Açık PnL", value: `${money(acc.unrealizedProfit)} USDT`, color: pnlColor(acc.unrealizedProfit) },
              ].map((c) => (
                <div key={c.label} className="rounded-xl border p-3 text-center" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{c.label}</div>
                  <div className="font-mono-term text-sm font-bold mt-1" style={{ color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          <div className="p-4 sm:p-5 space-y-3">
            {positions.length === 0 ? (
              <div className="py-8 text-center text-sm flex flex-col items-center gap-2" style={{ color: "var(--text-muted)" }}>
                <Wallet className="w-6 h-6" /> Açık pozisyon yok
              </div>
            ) : (
              positions.map((p, i) => {
                const isLong = p.positionSide === "LONG";
                return (
                  <div key={`${p.symbol}-${i}`} data-testid="position-card" className="rounded-xl border p-4 flex items-center justify-between gap-3 flex-wrap"
                    style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)" }}>
                    <div className="flex items-center gap-2">
                      {isLong ? <TrendingUp className="w-4 h-4" style={{ color: "var(--long)" }} /> : <TrendingDown className="w-4 h-4" style={{ color: "var(--short)" }} />}
                      <span className="font-bold text-sm">{p.symbol}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ color: isLong ? "var(--long)" : "var(--short)", background: isLong ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)" }}>{p.positionSide}</span>
                      {p.leverage && <span className="text-[10px] font-mono-term px-1.5 py-0.5 rounded" style={{ background: "rgba(59,130,246,0.15)", color: "#93C5FD" }}>{p.leverage}x</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs font-mono-term">
                      <div className="text-center"><div className="text-[9px] uppercase" style={{ color: "var(--text-muted)" }}>Giriş</div><div>{money(p.entryPrice)}</div></div>
                      <div className="text-center"><div className="text-[9px] uppercase" style={{ color: "var(--text-muted)" }}>Mark</div><div>{money(p.markPrice)}</div></div>
                      <div className="text-center"><div className="text-[9px] uppercase" style={{ color: "var(--text-muted)" }}>Miktar</div><div>{money(p.quantity)}</div></div>
                      <div className="text-center"><div className="text-[9px] uppercase" style={{ color: "var(--text-muted)" }}>PnL</div><div className="font-bold" style={{ color: pnlColor(p.pnl) }}>{money(p.pnl)}</div></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
